import type { AuthUser } from '@supabase/supabase-js'
import { logInfo, logWarn, logError } from './logger'
import { normalizeFirstNameFromFullName } from './name'

/**
 * Supabase can return a user object for duplicate email signups when email
 * enumeration protection is enabled. Those responses have no identities and
 * should not trigger onboarding side effects like welcome email sends.
 */
export function shouldSendWelcomeEmailForEmailSignup(
  user: Pick<AuthUser, 'identities'> | null | undefined
): boolean {
  if (!user) {
    return false
  }

  return Array.isArray(user.identities) && user.identities.length > 0
}

/**
 * Send welcome email to new user (fire-and-forget)
 *
 * This helper is used by both email/password signup AND OAuth callback flows.
 * The server-side function enforces idempotency via DB, so it's safe to call
 * multiple times for the same user.
 *
 * Does not block signup flow - errors are logged but not thrown.
 *
 * @param user - Supabase auth user object
 * @param accessToken - Valid session access token
 * @param displayName - Optional display name (will derive from user metadata if not provided)
 */
export async function sendWelcomeEmailIfPossible(
  user: AuthUser,
  accessToken: string,
  displayName?: string
): Promise<void> {
  try {
    // Derive display name from multiple sources
    const name =
      displayName ||
      user.user_metadata?.display_name ||
      user.user_metadata?.full_name ||
      user.email?.split('@')[0] ||
      ''

    // Normalize first name
    const firstName = normalizeFirstNameFromFullName(name)
    if (!firstName) {
      logWarn('WelcomeEmail', 'Cannot send welcome email - invalid name', {
        userId: user.id,
        name,
      })
      return
    }

    // Validate email
    if (!user.email) {
      logWarn('WelcomeEmail', 'Cannot send welcome email - no email', {
        userId: user.id,
      })
      return
    }

    // Validate access token
    if (!accessToken) {
      logWarn('WelcomeEmail', 'Cannot send welcome email - no access token', {
        userId: user.id,
      })
      return
    }

    // Call Netlify function
    const response = await fetch('/.netlify/functions/send-welcome-email', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        name,
        email: user.email,
      }),
    })

    const responseText = await response.text()
    let result: {
      status?: string
      messageId?: string
      error?: string
      message?: string
    } | null = null
    try {
      result = responseText ? JSON.parse(responseText) : null
    } catch {
      result = null
    }

    if (response.ok) {
      if (result?.status === 'sent') {
        logInfo('WelcomeEmail', 'Welcome email sent successfully', {
          userId: user.id,
          messageId: result.messageId,
        })
      } else if (result?.status === 'skipped_already_sent') {
        logInfo('WelcomeEmail', 'Welcome email already sent', {
          userId: user.id,
        })
      } else if (result?.status === 'disabled') {
        logInfo('WelcomeEmail', 'Welcome email feature disabled')
      } else {
        logWarn('WelcomeEmail', 'Unexpected success response payload', {
          userId: user.id,
          responseText: responseText.slice(0, 200),
        })
      }
    } else {
      logError('WelcomeEmail', 'Failed to send welcome email', {
        status: response.status,
        error: result?.error || response.statusText || 'Unknown error',
        responseText: responseText.slice(0, 200),
        userId: user.id,
      })
    }
  } catch (error) {
    // Log but don't throw - welcome email should never break signup/login
    logError('WelcomeEmail', 'Error sending welcome email', {
      error,
      userId: user.id,
    })
  }
}
